import type { SdkWidgetSettings, SdkPurpose, SdkConsentState } from '../types';
import { el, on } from './dom-utils';
import { createVeridionBranding } from './branding';

export interface ConsentSettingsPanelCallbacks {
  onWithdraw: () => Promise<void>;
  onChangeConsent: () => void;
  onClose: () => void;
}

/**
 * Renders the "Cookie settings" style panel: current consent state per purpose,
 * consent date and reference, withdraw and change consent actions.
 */
export function createConsentSettingsPanel(
  settings: SdkWidgetSettings,
  purposes: SdkPurpose[],
  consentStates: SdkConsentState[],
  subjectId: string,
  callbacks: ConsentSettingsPanelCallbacks,
): HTMLElement {
  const stateByPurpose = new Map(consentStates.map((s) => [s.purposeId, s]));

  const overlay = el('div', { className: 'vrd-overlay vrd-consent-panel-overlay' });
  const panel = el('div', { className: 'vrd-consent-panel' });
  overlay.appendChild(panel);

  const closeBtn = el('button', { className: 'vrd-modal__close', 'aria-label': 'Close' });
  closeBtn.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  const header = el(
    'div',
    { className: 'vrd-consent-panel__header' },
    el('h2', { className: 'vrd-consent-panel__title', textContent: 'Cookie settings' }),
    closeBtn,
  );
  panel.appendChild(header);

  const body = el('div', { className: 'vrd-consent-panel__body' });

  const stateList = el('div', { className: 'vrd-consent-panel__state-list' });
  stateList.appendChild(el('p', { className: 'vrd-consent-panel__state-heading', textContent: 'Your current state' }));

  for (const purpose of purposes) {
    const state = stateByPurpose.get(purpose.id);
    const status = state?.status ?? 'revoked';
    const row = el('div', { className: 'vrd-consent-panel__state-row' });
    const icon = el('span', {
      className: status === 'granted' ? 'vrd-consent-panel__icon vrd-consent-panel__icon--granted' : 'vrd-consent-panel__icon vrd-consent-panel__icon--revoked',
      'aria-hidden': 'true',
    });
    icon.textContent = status === 'granted' ? '✓' : '○';
    const label = el('span', { className: 'vrd-consent-panel__state-label', textContent: purpose.name });
    const dateStr = state?.lastUpdatedUtc
      ? formatConsentDate(state.lastUpdatedUtc)
      : '—';
    const refStr = state ? `${subjectId.slice(0, 8)}…` : '—';
    row.appendChild(icon);
    row.appendChild(label);
    row.appendChild(el('span', { className: 'vrd-consent-panel__state-meta', textContent: `${dateStr} · ${refStr}` }));
    stateList.appendChild(row);
  }
  body.appendChild(stateList);

  const details = el('div', { className: 'vrd-consent-panel__details' });
  const detailsToggle = el('button', {
    type: 'button',
    className: 'vrd-consent-panel__details-toggle',
    textContent: 'Hide details',
  });
  const detailsContent = el('div', { className: 'vrd-consent-panel__details-content' });

  const latestState = consentStates.length > 0
    ? consentStates.reduce((a, b) => (new Date(b.lastUpdatedUtc) > new Date(a.lastUpdatedUtc) ? b : a))
    : null;
  const consentDate = latestState ? formatConsentDate(latestState.lastUpdatedUtc) : '—';
  detailsContent.appendChild(el('p', { className: 'vrd-consent-panel__detail-row' }, el('strong', { textContent: 'Consent date: ' }), consentDate));
  detailsContent.appendChild(el('p', { className: 'vrd-consent-panel__detail-row' }, el('strong', { textContent: 'Your consent ID: ' }), subjectId));

  let detailsExpanded = true;
  detailsToggle.textContent = 'Hide details';
  function toggleDetails(): void {
    detailsExpanded = !detailsExpanded;
    detailsContent.classList.toggle('vrd-consent-panel__details-content--hidden', !detailsExpanded);
    detailsToggle.textContent = detailsExpanded ? 'Hide details' : 'Show details';
  }
  on(detailsToggle, 'click', toggleDetails);

  details.appendChild(detailsToggle);
  details.appendChild(detailsContent);
  body.appendChild(details);

  const withdrawBtn = el('button', { className: 'vrd-btn vrd-btn--secondary' }) as HTMLButtonElement;
  withdrawBtn.appendChild(document.createTextNode('Withdraw your consent'));
  withdrawBtn.appendChild(el('span', { className: 'vrd-btn__spinner', 'aria-hidden': 'true' }));

  const changeBtn = el('button', { className: 'vrd-btn vrd-btn--primary', textContent: 'Change your consent' }) as HTMLButtonElement;
  const actions = el('div', { className: 'vrd-consent-panel__actions' }, withdrawBtn, changeBtn);
  body.appendChild(actions);

  const branding = createVeridionBranding(settings.theme);
  body.appendChild(branding);

  panel.appendChild(body);

  on(closeBtn, 'click', callbacks.onClose);
  on(withdrawBtn, 'click', async () => {
    withdrawBtn.disabled = true;
    changeBtn.disabled = true;
    withdrawBtn.classList.add('vrd-btn--loading');
    try { await callbacks.onWithdraw(); } finally {
      withdrawBtn.disabled = false;
      changeBtn.disabled = false;
      withdrawBtn.classList.remove('vrd-btn--loading');
    }
  });
  on(changeBtn, 'click', callbacks.onChangeConsent);
  on(overlay, 'click', (e) => {
    if (e.target === overlay) callbacks.onClose();
  });
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      callbacks.onClose();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  return overlay;
}

function formatConsentDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}
